import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { GrTest } from "react-icons/gr";

import ListPage from "../../../shared/components/list-page/list-page";
import { ITableRowData, IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import { roleWrite, roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { getAllData, deleteMultiple, postSingle, IDetailPageData, postSingleNonTable } from "../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import { databaseConnectionUrl } from "../../constants/backend-constants";
import "./database-connection.scss";

const columns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
    controlled: true,
    errorMsg: "The name is already taken"
  },
  {
    key: "connection_string",
    displayName: "Connection string",
    dataType: {
      type: "text",
    },
  },
  {
    key: "valid",
    displayName: "Validated",
    dataType: {
      type: "text",
    },
  },
];

const DatabaseConnection = () => {
  const [data, setData] = useState<IDetailPageData>();
  const authCtx = useContext(AuthContext);
  const [dataToBeSaved, setDataToBeSaved] = useState<IDetailPageData>({rowData: []});
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=name asc");
  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttp(getAllData, true, authCtx.token, "databaseConnection");
  const { sendRequest: postSingleRequest, status: allStatusPost, data: postData, error: errorPost } = useHttp(postSingle, false, authCtx.token, "databaseConnection");
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token, "databaseConnection");
  const { sendRequest: postTestDatabaseConnection } = useHttpNonTable(postSingleNonTable, false, authCtx.token);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    getSortBy("database_connections", "?order_by=name asc");
  }, [getSortBy]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData({rowData: getSortByData});
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      dataToBeSaved && postSingleRequest(`database_connections${sortedBy}`, { rowData: dataToBeSaved });
    }
  }, [allStatusDelete]);

  useEffect(() => {
    if (allStatusPost === "completed" && !errorPost) {
      setData({rowData: postData});
      setDataToBeSaved({rowData: []});
      authCtx.setOnboardingPlatformToggleHandler();
    }
  }, [allStatusPost]);

  const handleOnDelete = (ids: string[]) => {
    deleteMultipleRequest("database_connections", { ids });
  };

  const saveData = (rowData: ITableRowData[], ids?: string[]) => {
    // temporary solution before we have a proper API call
    if (ids && ids.length !== 0) {
      rowData && setDataToBeSaved({rowData});
      deleteMultipleRequest(`database_connections${sortedBy}`, { ids });
      return;
    }

    const filteredData = (rowData ?? []).map(
      ({ id, name, connection_string }) => ({ id, name, connection_string })
    );
    
    postSingleRequest(`database_connections${sortedBy}`, { rowData: filteredData });
  };

  const sortBy = (sortingQuery: string) => {
    setSortedBy(sortingQuery);
    getSortBy("database_connections", `${sortingQuery}`);
  };

  const renderExtraPopUpButtons = (data: ITableRowData) => {
    return (
      <Tooltip content={"Test the connection"} direction="top" delay={350}  >
        <Button
          className="databaseConnections-testButton"
          variant="primary"
          onClick={() => postTestDatabaseConnection("database_connection_check", { connection_string : data.connection_string })}
        >
          <GrTest />
        </Button>
      </Tooltip>
    );
  };

  return (
    <div className="databaseConnections">
      {validateLoadingStatusNew([getSortByStatus, allStatusPost, allStatusDelete]) && <Spinner />}
      <ListPage
        title={"Database connections"}
        columns={columns}
        sortBy={sortBy}
        defaultSortCol={"name"}
        unblockInput={unblockInput}
        pageMode={"editable"}
        saveData={saveData}
        handleOnDelete={handleOnDelete}
        fetchedData={data}
        localDeleteOn={true}
        hideButtons={["generate", "settings"]}
        dataUrl={databaseConnectionUrl}
        allStatusDelete={allStatusDelete}
        allStatus={getSortByStatus}
        allStatusPost={allStatusPost}
        allStatusPostError={errorPost}
        renderExtraPopUpButtons={renderExtraPopUpButtons}
        tooltipTxt={"Database connections are needed to establish connection to remote db's"}
        uniqueKeys={["name"]}
      />
    </div>
  );
};

export default DatabaseConnection;
